import React, { useCallback, useEffect, useState } from "react";
import { Box, Card, CardContent, CardHeader } from "@material-ui/core";
import { find, propEq } from "ramda";
import Grid from "@material-ui/core/Grid";
import { notBlank } from "../../util/ext";
import LabeledReadOnly from "../../components/LabeledReadOnly";
import { makeStyles } from "@material-ui/core/styles";
import ReadOnlyField from "../../components/ReadOnlyField";
import { DateTime } from "luxon";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import DownloadIcon from "@material-ui/icons/SaveAlt";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import {
  generateFilename,
  getDownloadUrl,
  useJobExecutionDetails,
  useStopJobExecution,
} from "./processingDiscoveryServices";
import { useSelector } from "react-redux";

const useStyles = makeStyles(theme => ({
  header: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  content: {
    paddingTop: 0,
  },
}));

const MetadataFileDiscoveryEventCard = ({
  metadataFileDiscoveryEvent,
  contentProviderList,
  contentProviderEndpointList,
}) => {
  const classes = useStyles();
  const auth = useSelector(state => state.auth);

  const [jobExecutionDetails, setJobExecutionDetails] = useState({});
  const jobExecutionDetailsResponse = useJobExecutionDetails(
    metadataFileDiscoveryEvent.executionId,
  );
  useEffect(() => {
    if (jobExecutionDetailsResponse.isSuccess) {
      setJobExecutionDetails(jobExecutionDetailsResponse.data);
    }
  }, [jobExecutionDetailsResponse.isSuccess, jobExecutionDetailsResponse.data]);

  const stopJobExecution = useStopJobExecution();
  const handleStopExecution = useCallback(async () => {
    console.log("Handling Stop Execution...");
    try {
      await stopJobExecution.mutateAsync({
        resourceIds: {
          executionId: metadataFileDiscoveryEvent.executionId,
        },
      });
    } catch (err) {
      console.log(err);
    }
  }, [stopJobExecution, metadataFileDiscoveryEvent.executionId]);

  const findContentProviderName = id => {
    const provider = find(propEq("contentProviderId", id), contentProviderList);
    return notBlank(provider) ? provider.name : "...";
  };

  const findContentProviderEndpointName = id => {
    const providerEndpoint = find(
      propEq("contentProviderEndpointId", id),
      contentProviderEndpointList,
    );
    return notBlank(providerEndpoint) ? providerEndpoint.name : "...";
  };

  const [metadataDownloadUrl, setMetadataDownloadUrl] = useState(null);
  const [downloadFileName, setDownloadFileName] = useState(null);
  useEffect(() => {
    async function fetchData() {
      const location = notBlank(
        metadataFileDiscoveryEvent.metadataFileArchiveLocation,
      )
        ? metadataFileDiscoveryEvent.metadataFileArchiveLocation
        : metadataFileDiscoveryEvent.metadataFileLocation;

      const url = await getDownloadUrl(auth, location);
      setMetadataDownloadUrl(url);
      setDownloadFileName(generateFilename(location));
    }

    fetchData();
  }, [auth, metadataFileDiscoveryEvent]);

  return (
    <Card raised elevation={4} style={{ height: "100%" }}>
      <CardHeader
        titleTypographyProps={{
          variant: "h6",
          color: "primary",
        }}
        title="Details"
        classes={{ root: classes.header }}
      />
      <CardContent classes={{ root: classes.content }}>
        <Grid container spacing={3}>
          <Grid item sm={5}>
            <LabeledReadOnly label="Event Timestamp">
              <ReadOnlyField>
                {DateTime.fromISO(metadataFileDiscoveryEvent.eventTimestamp)
                  .toUTC()
                  .toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)}
              </ReadOnlyField>
            </LabeledReadOnly>
          </Grid>

          <Grid item sm={5}>
            <LabeledReadOnly label="Job Id">
              <ReadOnlyField>{metadataFileDiscoveryEvent.jobId}</ReadOnlyField>
            </LabeledReadOnly>
          </Grid>

          <Grid item sm={1}>
            <LabeledReadOnly label="Status">
              <Box display="flex" alignSelf="center">
                <Box>
                  <ReadOnlyField>{jobExecutionDetails.status}</ReadOnlyField>
                </Box>
                {jobExecutionDetails.status === "RUNNING" && (
                  <Box mt={-2.5}>
                    <Tooltip title="Stop Execution">
                      <IconButton onClick={() => handleStopExecution()}>
                        <HighlightOffIcon color="primary" fontSize="large" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            </LabeledReadOnly>
          </Grid>

          <Grid item sm={5}>
            <LabeledReadOnly label="Content Provider">
              <Box>
                <ReadOnlyField>
                  {findContentProviderName(
                    metadataFileDiscoveryEvent.contentProviderId,
                  )}
                </ReadOnlyField>

                {notBlank(metadataFileDiscoveryEvent.contentProviderId) && (
                  <ReadOnlyField>
                    {metadataFileDiscoveryEvent.contentProviderId}
                  </ReadOnlyField>
                )}
              </Box>
            </LabeledReadOnly>
          </Grid>

          <Grid item sm={5}>
            <LabeledReadOnly label="Content Provider Enpoint">
              <Box>
                <ReadOnlyField>
                  {findContentProviderEndpointName(
                    metadataFileDiscoveryEvent.contentProviderEndpointId,
                  )}
                </ReadOnlyField>

                {notBlank(
                  metadataFileDiscoveryEvent.contentProviderEndpointId,
                ) && (
                  <ReadOnlyField>
                    {metadataFileDiscoveryEvent.contentProviderEndpointId}
                  </ReadOnlyField>
                )}
              </Box>
            </LabeledReadOnly>
          </Grid>

          <Grid item sm={12}>
            <LabeledReadOnly label="Metadata File Location">
              <Box display="flex" alignSelf="center">
                <Box>
                  <ReadOnlyField>
                    {notBlank(
                      metadataFileDiscoveryEvent.metadataFileArchiveLocation,
                    )
                      ? metadataFileDiscoveryEvent.metadataFileArchiveLocation
                      : metadataFileDiscoveryEvent.metadataFileLocation}
                  </ReadOnlyField>
                </Box>
                {notBlank(metadataDownloadUrl) && (
                  <Box mt={-2.5}>
                    <Tooltip title="Download">
                      <IconButton
                        href={metadataDownloadUrl}
                        target="_blank"
                        download={downloadFileName}
                      >
                        <DownloadIcon color="primary" fontSize="large" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            </LabeledReadOnly>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default MetadataFileDiscoveryEventCard;
